import EventHandler from '@pixelunion/events';
import ThemeUtils from '../utils/ThemeUtils';

export default class ImageZoom {
  constructor(el) {
    this.events = new EventHandler();
    this.enableZoom = this.enableZoom.bind(this);
    this.disableZoom = this.disableZoom.bind(this);
    this._bindEvents = this._bindEvents.bind(this);
    this._unbindEvents = this._unbindEvents.bind(this);
    this._bindLayoutEvent = this._bindLayoutEvent.bind(this);
    this._unbindLayoutEvent = this._unbindLayoutEvent.bind(this);
    this._layoutHandler = this._layoutHandler.bind(this);
    this._toggleZoom = this._toggleZoom.bind(this);
    this._resetZoom = this._resetZoom.bind(this);
    this._positionZoom = this._positionZoom.bind(this);
    this.unload = this.unload.bind(this);
    this.el = el;
    this.img = el.querySelector('img');
    this.framerate = 60;
    this.scale = 1;
    this.zoomable = false;
    this.zoomed = false;
    this.requestAnimationFrameId = null;

    this.layoutHandler = this._layoutHandler.bind(this);
    this.resetZoom = this._resetZoom.bind(this);

    this._bindLayoutEvent();
  }

  enableZoom() {
    this.el.classList.add('product-image-zoomable');
    this._bindEvents();
  }

  disableZoom() {
    this.el.classList.remove('product-image-zoomable');
    this._unbindEvents();
    this._resetZoom();
  }

  _bindEvents() {
    this.productZoomClick = this.events.register(this.el, 'click', event => this._toggleZoom(event.clientX, event.clientY));
    window.addEventListener('resize', this.resetZoom);
  }

  _unbindEvents() {
    this.events.unregister(this.productZoomClick);
    window.removeEventListener('resize', this.resetZoom);
  }

  _bindLayoutEvent() {
    this.requestAnimationFrameId = window.requestAnimationFrame(this.layoutHandler);
  }

  _unbindLayoutEvent() {
    window.cancelAnimationFrame(this.requestAnimationFrameId);
  }

  _layoutHandler() {
    const scale = this.img.naturalWidth / this.el.getBoundingClientRect().width;
    const zoomable = scale >= 1.25;

    if (zoomable === this.zoomable) {
      this.requestAnimationFrameId = window.requestAnimationFrame(this.layoutHandler);
      return;
    }
    this.scale = scale;
    this.zoomable = zoomable;

    if (!this.zoomable) {
      this.disableZoom();
    } else {
      this.enableZoom();
    }

    this.requestAnimationFrameId = window.requestAnimationFrame(this.layoutHandler);
  }

  _toggleZoom(clientX, clientY) {
    if (!this.zoomed) {
      this.zoomMouseMove = this.events.register(
        this.el,
        'mousemove',
        ThemeUtils.throttle(
          event => this._positionZoom(event.clientX, event.clientY),
          1000 / this.framerate,
        ),
      );
      this.zoomMouseOut = this.events.register(this.el, 'mouseout', this._resetZoom);
      this.el.classList.add('product-image-zoomed');
      this.img.style.transform = `scale(${this.scale})`;
      this._positionZoom(clientX, clientY);
      this.zoomed = true;
    } else {
      this._resetZoom();
    }
  }

  _resetZoom() {
    if (this.zoomMouseMove) {
      this.events.unregister(this.zoomMouseMove);
    }
    if (this.zoomMouseOut) {
      this.events.unregister(this.zoomMouseOut);
    }
    this.el.classList.remove('product-image-zoomed');
    this.img.style.transform = '';
    this.zoomed = false;
  }

  _positionZoom(clientX, clientY) {
    const figRect = this.el.getBoundingClientRect();
    const imgRect = this.img.getBoundingClientRect();
    const figHalfWidth = figRect.width / 2;
    const figHalfHeight = figRect.height / 2;
    const centerX = figRect.left + figHalfWidth;
    const centerY = figRect.top + figHalfHeight;
    const widthDiff = (imgRect.width / 2) - figHalfWidth;
    const heightDiff = (imgRect.height / 2) - figHalfHeight;
    const translateX = (((centerX - clientX) / figHalfWidth) * widthDiff) / this.scale;
    const translateY = (((centerY - clientY) / figHalfHeight) * heightDiff) / this.scale;

    this.img.style.transform = `scale(${this.scale}) translate(${translateX}px, ${translateY}px)`;
  }

  unload() {
    this._unbindLayoutEvent();
    this.disableZoom();
  }
}
